import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _typeof from "@babel/runtime/helpers/typeof";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "className", "customOptionValue", "customOptionLabel", "selectLabel", "helperText", "noOptionsText", "options", "disabled", "groupBy", "blankOption", "tabIndex", "onChange", "showFieldPrefixIcon", "value", "disableCloseOnWindowResize", "onFocus", "onBlur", "required", "multiSelect", "loading", "loadingEndedToOpenMenu", "theme", "onOpen", "onClose", "errorMessage", "successMessage", "noEmptySelection", "maxMenuHeight", "getOptionDisabled", "inputProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { visuallyHidden } from '@mui/utils';
import { useDidMountEffect } from '../../utils/useDidMountEffect';
import Divider from '@mui/material/Divider';
import { EmbersMenuItem } from '../../menuItem/menuItem';
import { EmbersStatusIcon } from '../../statusIcon';
import { DarkStatus } from '../../statusIcon/darkStatus';
import { scrollMenuItemIntoView } from '../../menuItem/scrollMenuItemIntoViewUtil';
import { InputSelectContainer, InputSelectHelperText, InputSelectDarkLabel, StyledInputSelectInput, StyledInputSelectLaunchButton, InputSelectFieldset, InputSelectAlertFocusFieldset, InputSelectAlertText, SelectGroupSubheader, StyledInputSelectLabel } from './inputSelectStyles';
import { EmbersMenu } from '../../menu';
import { EmbersTag } from '../../tag';
import { EmbersLoading } from '../../loading';
import { useIntl } from 'react-intl';
import { getTextWidth } from '../../utils/getTextWidth';
import { prometheanFont } from '../../styles';
import { isTouchDevices } from '../../utils/platformUtils';
import isEqual from 'lodash.isequal';
import { useFieldFocusOnWindowBlur } from '../../utils/useFieldFocusOnWindowBlur';
var DIVIDER_ID_PREFIX = 'divider_';
var GROUP_HEADER_ID_PREFIX = 'groupHeader_';
function InputSelect(props, ref) {
  var id = props.id,
    className = props.className,
    customOptionValue = props.customOptionValue,
    customOptionLabel = props.customOptionLabel,
    selectLabel = props.selectLabel,
    helperText = props.helperText,
    noOptionsText = props.noOptionsText,
    options = props.options,
    disabled = props.disabled,
    groupBy = props.groupBy,
    blankOption = props.blankOption,
    _props$tabIndex = props.tabIndex,
    tabIndex = _props$tabIndex === void 0 ? 0 : _props$tabIndex,
    onChange = props.onChange,
    showFieldPrefixIcon = props.showFieldPrefixIcon,
    value = props.value,
    disableCloseOnWindowResize = props.disableCloseOnWindowResize,
    onFocus = props.onFocus,
    onBlur = props.onBlur,
    required = props.required,
    _props$multiSelect = props.multiSelect,
    multiSelect = _props$multiSelect === void 0 ? false : _props$multiSelect,
    _props$loading = props.loading,
    loading = _props$loading === void 0 ? false : _props$loading,
    _props$loadingEndedTo = props.loadingEndedToOpenMenu,
    loadingEndedToOpenMenu = _props$loadingEndedTo === void 0 ? true : _props$loadingEndedTo,
    theme = props.theme,
    onOpen = props.onOpen,
    onClose = props.onClose,
    errorMessage = props.errorMessage,
    successMessage = props.successMessage,
    noEmptySelection = props.noEmptySelection,
    maxMenuHeight = props.maxMenuHeight,
    getOptionDisabled = props.getOptionDisabled,
    _props$inputProps = props.inputProps,
    inputProps = _props$inputProps === void 0 ? {} : _props$inputProps,
    restProps = _objectWithoutProperties(props, _excluded);
  var intl = useIntl();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    menuIsOpen = _useState2[0],
    setMenuIsOpen = _useState2[1];
  var menuAnchorRef = useRef(null);
  var _inputRef = useRef(null);
  var searchResultsDelay = 1100;
  var isDarkMode = theme === 'dark';
  var getOptionToTest = function getOptionToTest() {
    // if this is an asynch select, we won't have options right away, but we might
    //  have a value - which we need to parse properly as object or string
    if ((!options || Array.isArray(options) && options.length === 0) && value) {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          return value[0];
        }
      } else {
        return value;
      }
    }
    if (Array.isArray(options) && options.length > 0) {
      return options[0];
    }
    return null;
  };
  var optionsAreObjects = function optionsAreObjects() {
    var testOption = getOptionToTest();
    // note that null is an object :-\
    return !!testOption && _typeof(testOption) === 'object';
  };
  var _useState3 = useState(void 0),
    _useState4 = _slicedToArray(_useState3, 2),
    searchTimeout = _useState4[0],
    setSearchTimeout = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    searchResults = _useState6[0],
    setSearchResults = _useState6[1];
  var _useState7 = useState(options),
    _useState8 = _slicedToArray(_useState7, 2),
    filteredOptions = _useState8[0],
    setFilteredOptions = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    useInputAsFilter = _useState10[0],
    setUseInputAsFilter = _useState10[1];
  // we have to update the two fields in the focusInfo simultaneously to prevent flicker/extra renders
  var _useState11 = useState({
      userClicked: false,
      inputHasFocus: false
    }),
    _useState12 = _slicedToArray(_useState11, 2),
    focusInfo = _useState12[0],
    setFocusInfo = _useState12[1];
  var _useState13 = useState(''),
    _useState14 = _slicedToArray(_useState13, 2),
    currentActiveDescendentId = _useState14[0],
    setCurrentActiveDescendentId = _useState14[1];
  var itemIdsRef = useRef([]);
  var _useState15 = useState(optionsAreObjects()),
    _useState16 = _slicedToArray(_useState15, 2),
    optionsAreMaps = _useState16[0],
    setOptionsAreMaps = _useState16[1];
  var _useState17 = useState(30),
    _useState18 = _slicedToArray(_useState17, 2),
    inputMinWidth = _useState18[0],
    setInputMinWidth = _useState18[1];
  var inputMinWidthRef = useRef(30);

  // for the selection info we need a useState() to trigger the render on changes
  //  and a useRef() for non-render-time logic - specifically in handleCloseMenuRequest
  var _useState19 = useState([]),
    _useState20 = _slicedToArray(_useState19, 2),
    selectedOptionIndexes = _useState20[0],
    setSelectedOptionIndexes = _useState20[1];
  var hasInitialSelection = function hasInitialSelection() {
    if (!value) {
      return false;
    }
    return value.length > 0;
  };
  var hasSelectionRef = useRef(hasInitialSelection());
  var isTabFocused = function isTabFocused() {
    return focusInfo.inputHasFocus && !focusInfo.userClicked;
  };
  var noOptionsLabel = noOptionsText || intl.formatMessage({
    id: 'embers.select.noOptions',
    defaultMessage: 'No options'
  });
  var _useState21 = useState(false),
    _useState22 = _slicedToArray(_useState21, 2),
    blankOptionIsSelected = _useState22[0],
    setBlankOptionIsSelected = _useState22[1];
  var updateFocusInfo = function updateFocusInfo(newFocusInfo) {
    if (newFocusInfo.userClicked) {
      setCurrentActiveDescendentId('');
    }
    setFocusInfo(function (prevFocusInfo) {
      var focusInfo = _objectSpread(_objectSpread({}, prevFocusInfo), newFocusInfo);
      return focusInfo;
    });
  };
  var hadClickFocusOnInputBlurRef = useFieldFocusOnWindowBlur(function (newFocusInfo) {
    return updateFocusInfo({
      userClicked: newFocusInfo
    });
  });
  var updateSelectionInfo = function updateSelectionInfo(newSelectedIndexes) {
    setSelectedOptionIndexes(newSelectedIndexes);
    hasSelectionRef.current = newSelectedIndexes.length > 0;
  };
  useEffect(function () {
    // this effect is primarily for calling setOptionsAreMaps but it also is checking for a
    //  dev error where the customOptionValue prop does not match the data (the options)
    var optionToTest = getOptionToTest();
    if (optionsAreObjects() && optionToTest !== null && customOptionValue && typeof optionToTest[customOptionValue] === 'undefined') {
      console.error("InputSelect id \"".concat(id, "\" error: 'customOptionValue' \"").concat(customOptionValue === null || customOptionValue === void 0 ? void 0 : customOptionValue.toString(), "\" is not present on options"));
    }
    setOptionsAreMaps(optionsAreObjects());
  }, [options]);
  var getOptionLabel = function getOptionLabel(option) {
    if (typeof option === 'string') return option;
    return customOptionLabel ? option[customOptionLabel] : option.label;
  };
  var getOptionValue = function getOptionValue(option) {
    if (typeof option === 'string') return option;
    return customOptionValue ? option[customOptionValue] : option.value;
  };

  // in order to know that the user hit a space (which is used by multiselect
  //  to select/deselect an item) we have the spaceInputWasMultiSelectAction ref
  //  whose sole purpose is to hold the information between the keydown and the change event
  var spaceInputWasMultiSelectAction = useRef(false);
  var _useState23 = useState(!Array.isArray(value) && value && getOptionLabel(value) ? getOptionLabel(value) : ''),
    _useState24 = _slicedToArray(_useState23, 2),
    inputValue = _useState24[0],
    setInputValue = _useState24[1];
  var labelIsInCorner = menuIsOpen || hasSelectionRef.current || focusInfo.inputHasFocus || inputValue;

  // this effect is to reset the inputValue if value changes after instantiation,
  //  for external reasons
  useEffect(function () {
    if (!Array.isArray(value) && !useInputAsFilter) {
      var newInputValue = value && getOptionLabel(value) ? getOptionLabel(value) : '';
      if (inputValue !== newInputValue) {
        setInputValue(newInputValue);
      }
    }
    // handle initial value as blankOption
    if (blankOption && isEqual(value, blankOption) && !blankOptionIsSelected) {
      setBlankOptionIsSelected(true);
    }
  }, [value]);

  // this effect is to keep the selectedOptionIndexes in sync with
  // value and options if they change
  useEffect(function () {
    var selectedIndexes = new Array();
    if (value) {
      var selectedOptions = Array.isArray(value) ? value : [value];
      selectedOptions.forEach(function (selectedOption) {
        var aSelectedValue = getOptionValue(selectedOption);
        var selectedIndex = options.findIndex(function (option) {
          // the blankOption is basically a button and cannot be selected
          if (blankOption && isEqual(option, blankOption)) {
            return false;
          }
          if (optionsAreMaps && option.isDivider) {
            return false;
          }
          return aSelectedValue === getOptionValue(option);
        });
        if (selectedIndex !== -1) {
          selectedIndexes.push(selectedIndex);
        }
      });
    }
    updateSelectionInfo(selectedIndexes);
  }, [value, options]);

  // this effect is to calc the minWidth of the input - it is sized to fit the selectLabel
  useEffect(function () {
    var fontDesc = {
      family: prometheanFont,
      size: 16,
      weight: 400,
      // .02em = .32px
      letterSpacing: 0.32
    };
    var measureLabel = required ? selectLabel + '&nbsp;*' : selectLabel;
    var maxLen = getTextWidth(measureLabel, fontDesc);
    // the input gets an extra five pixels of lebensraum
    inputMinWidthRef.current = Math.round(maxLen + 5);
  }, [selectLabel]);
  useEffect(function () {
    if (labelIsInCorner) {
      setInputMinWidth(inputMinWidthRef.current);
    } else {
      setInputMinWidth(30);
    }
  }, [labelIsInCorner]);
  var handleItemClick = function handleItemClick(e, clickedOption) {
    var itemIndex = options.indexOf(clickedOption);
    if (multiSelect) {
      var newSelectedIndexes;
      if (selectedOptionIndexes.includes(itemIndex)) {
        newSelectedIndexes = selectedOptionIndexes.filter(function (v) {
          return v !== itemIndex;
        });
      } else {
        newSelectedIndexes = selectedOptionIndexes.slice();
        newSelectedIndexes.push(itemIndex);
      }
      updateSelectionInfo(newSelectedIndexes);
      setBlankOptionIsSelected(false);
      if (onChange) {
        var selectedOptions = newSelectedIndexes.map(function (index) {
          return options[index];
        });
        onChange(selectedOptions);
      }
      if (menuIsOpen) {
        var _inputRef$current;
        (_inputRef$current = _inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.focus();
      }
      if (e.type === 'click') {
        updateFocusInfo({
          userClicked: true
        });
      }
    } else {
      // single-select
      var newInputValue = getOptionLabel(clickedOption);
      if (blankOption && isEqual(clickedOption, blankOption) && hasSelectionRef.current) {
        updateSelectionInfo([]);
        setBlankOptionIsSelected(true);
        newInputValue = '';
        if (onChange) {
          onChange(blankOption);
        }
      } else if (selectedOptionIndexes.length === 0 || selectedOptionIndexes[0] !== itemIndex) {
        // only do something if this is not already selected
        updateSelectionInfo([itemIndex]);
        setBlankOptionIsSelected(false);
        if (onChange) {
          onChange(clickedOption);
        }
      }
      setUseInputAsFilter(false);
      setInputValue(newInputValue);
      handleCloseMenuRequest();
    }
  };
  var doesAnyOptionMatch = function doesAnyOptionMatch(filterString) {
    var lowFilter = filterString.toLowerCase();
    var foundOption = options.find(function (anOption) {
      var optionLabel = getOptionLabel(anOption).toLowerCase();
      return optionLabel.includes(lowFilter);
    });
    return !!foundOption;
  };

  // this effect is for creating the filteredOptions
  useEffect(function () {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    var filteredOptions = options.slice();
    if (inputValue && useInputAsFilter) {
      var lowerInput = inputValue.toLowerCase();
      filteredOptions = options.filter(function (anOption) {
        if (optionsAreMaps && (anOption.isDivider || anOption.isNotFiltered)) {
          return true;
        }
        var optionLabel = getOptionLabel(anOption).toLowerCase();
        return optionLabel.includes(lowerInput);
      });
      if (optionsAreMaps) {
        // check to make sure no extra dividers
        var undividedOptions = [];
        filteredOptions.forEach(function (anOption, index) {
          if (anOption.isDivider) {
            if (
            // don't want dividers at the beginning, the end, or after a divider
            undividedOptions.length > 0 && index < filteredOptions.length - 1 && !undividedOptions[undividedOptions.length - 1].isDivider) {
              undividedOptions.push(anOption);
            }
          } else {
            undividedOptions.push(anOption);
          }
        });
        filteredOptions = undividedOptions;
      }
      setSearchTimeout(setTimeout(function () {
        filteredOptions.length ? setSearchResults(intl.formatMessage({
          id: 'embers.select.optionsFound',
          defaultMessage: '{count} options found'
        }, {
          count: filteredOptions.length
        })) : inputValue && setSearchResults(noOptionsLabel);
      }, searchResultsDelay));
    } else {
      setSearchResults('');
    }
    setFilteredOptions(filteredOptions);
  }, [options, inputValue, useInputAsFilter]);

  // This effect is for async/loading - when done loading, we need to restore focus to the input.
  // But if the loading is not for select itself, it shouldn't open the menu.
  useDidMountEffect(function () {
    if (!loading && loadingEndedToOpenMenu) {
      var _inputRef$current2;
      (_inputRef$current2 = _inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
    }
  }, [loading]);
  var handleOpenMenuRequest = function handleOpenMenuRequest() {
    if (!menuIsOpen) {
      var _inputRef$current3;
      setMenuIsOpen(true);
      (_inputRef$current3 = _inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.focus();
      if (onOpen) {
        onOpen();
      }
    }
  };
  var handleCloseMenuRequest = function handleCloseMenuRequest() {
    setSearchResults('');
    setCurrentActiveDescendentId('');
    setMenuIsOpen(false);
    // clear the input if the user has not selected any of its search results
    if (multiSelect || !hasSelectionRef.current) {
      setInputValue('');
      setUseInputAsFilter(false);
    }
    // for single-select, when the menu is closing we restore the selected value (if there is one)
    if (!Array.isArray(value) && hasSelectionRef.current && value) {
      setInputValue(getOptionLabel(value));
      setUseInputAsFilter(false);
    }
    if (onClose) {
      onClose();
    }
  };
  var handleArrowIconClick = function handleArrowIconClick(e) {
    e.stopPropagation();
    updateFocusInfo({
      userClicked: true
    });
    if (menuIsOpen) {
      handleCloseMenuRequest();
    } else {
      handleOpenMenuRequest();
    }
  };
  var renderAlertText = function renderAlertText() {
    if (errorMessage || successMessage) {
      var alertContent;
      if (errorMessage) {
        if (isDarkMode) {
          alertContent = /*#__PURE__*/React.createElement(DarkStatus, {
            type: "error"
          }, errorMessage);
        } else {
          alertContent = /*#__PURE__*/React.createElement(EmbersStatusIcon, {
            type: "error",
            variant: "filled",
            size: "small"
          }, /*#__PURE__*/React.createElement("span", {
            className: classnames({
              'EmbersInputSelect-alertText-error': true
            })
          }, errorMessage));
        }
      } else if (successMessage) {
        if (isDarkMode) {
          alertContent = alertContent = /*#__PURE__*/React.createElement(DarkStatus, {
            type: "success"
          }, successMessage);
        } else {
          alertContent = /*#__PURE__*/React.createElement(EmbersStatusIcon, {
            type: "success",
            iconName: "icon-check",
            size: "small"
          }, /*#__PURE__*/React.createElement("span", {
            className: classnames({
              'EmbersInputSelect-alertText-success': true
            })
          }, successMessage));
        }
      }
      return /*#__PURE__*/React.createElement(InputSelectAlertText, {
        id: "menu-alertText-".concat(id)
        // todo: find out if we want errors to be assertive
        // aria-live="assertive"
        ,
        "aria-live": "polite",
        "aria-atomic": "true",
        className: classnames({
          'EmbersInputSelect-alertText-root': true,
          'EmbersInputSelect-alertText-disabled': disabled,
          'EmbersInputSelect-alertText-dark': isDarkMode
        })
      }, alertContent);
    }
    return undefined;
  };
  var renderHelperText = function renderHelperText() {
    if (helperText) {
      return /*#__PURE__*/React.createElement(InputSelectHelperText, {
        id: "menu-helpText-".concat(id),
        className: classnames({
          'EmbersInputSelect-helperText-root': true,
          'EmbersInputSelect-helperText-disabled': disabled,
          'EmbersInputSelect-helperText-dark': isDarkMode
        })
      }, /*#__PURE__*/React.createElement("div", {
        className: "EmbersInput-helperText-content"
      }, helperText));
    }
    return undefined;
  };
  var renderMenuItem = function renderMenuItem(option, index, itemIds, selectedValues) {
    if (optionsAreMaps && option.isDivider) {
      var dividerId = DIVIDER_ID_PREFIX + index;
      return /*#__PURE__*/React.createElement(Divider, {
        key: dividerId,
        id: dividerId
      });
    }
    var prefixIcon;
    if (optionsAreMaps && option.prefixIcon) {
      var icon = option.prefixIcon;
      if (typeof icon === 'string') prefixIcon = /*#__PURE__*/React.createElement("span", {
        className: icon,
        title: icon
      });else prefixIcon = icon;
    }
    var optionDisabled = getOptionDisabled === null || getOptionDisabled === void 0 ? void 0 : getOptionDisabled(option);
    var optionValue = getOptionValue(option);
    var itemId = id + '_' + optionValue + '_' + index;
    itemIds.push(itemId);
    var isSelected = !!optionValue && selectedValues.includes(optionValue);
    return /*#__PURE__*/React.createElement(EmbersMenuItem, {
      key: optionValue + '_' + index,
      id: itemId,
      className: classnames({
        'EmbersInputSelectItem-root': true
      }),
      isActiveDescendent: currentActiveDescendentId === itemId,
      value: optionValue,
      selected: isSelected,
      checkable: multiSelect,
      onCloseRequest: function onCloseRequest() {},
      onClick: function onClick(e) {
        handleItemClick(e, option);
      },
      prefixIcon: prefixIcon,
      "aria-posinset": index,
      "aria-setsize": itemIds.length,
      disabled: optionDisabled
    }, getOptionLabel(option));
  };
  var renderMenuItems = function renderMenuItems() {
    if (loading) {
      return [];
    }
    if (filteredOptions.length === 0) {
      var noOptionsID = "".concat(id, "-noOptions");
      itemIdsRef.current = [noOptionsID];
      return [/*#__PURE__*/React.createElement(EmbersMenuItem, {
        id: noOptionsID,
        key: "no-options",
        role: "presentation",
        value: "",
        selected: false,
        checkable: false,
        clickable: false
      }, noOptionsLabel)];
    }
    var selectedValues = selectedOptionIndexes.map(function (selectedOptionIndex) {
      return options[selectedOptionIndex] ? getOptionValue(options[selectedOptionIndex]) : null;
    }).filter(function (item) {
      return item;
    });
    var itemIds = [];
    var itemsArray = [];
    var blankItemCount = 0;
    if (blankOption && selectedOptionIndexes.length > 0) {
      blankItemCount = 1;
      itemsArray.push(renderMenuItem(blankOption, 0, itemIds, selectedValues));
    }
    if (groupBy) {
      var groupedOptions = new Map();
      filteredOptions.forEach(function (option) {
        var _groupedOptions$get;
        if (!groupedOptions.has(groupBy(option))) {
          groupedOptions.set(groupBy(option), []);
        }
        (_groupedOptions$get = groupedOptions.get(groupBy(option))) === null || _groupedOptions$get === void 0 || _groupedOptions$get.push(option);
      });
      var optionIndex = blankItemCount;
      groupedOptions.forEach(function (optionGroup, grpIndex) {
        var groupHeaderId = GROUP_HEADER_ID_PREFIX + grpIndex;
        itemsArray.push( /*#__PURE__*/React.createElement(SelectGroupSubheader, {
          key: groupHeaderId,
          id: groupHeaderId,
          className: "SelectGroupItem-root",
          disableSticky: true,
          disableGutters: true
        }, groupBy(optionGroup[0]).toUpperCase()));
        optionGroup.forEach(function (option) {
          itemsArray.push(renderMenuItem(option, optionIndex, itemIds, selectedValues));
          optionIndex++;
        });
      });
    } else {
      filteredOptions.forEach(function (option, index) {
        itemsArray.push(renderMenuItem(option, index + blankItemCount, itemIds, selectedValues));
      });
    }
    itemIdsRef.current = itemIds;
    return itemsArray;
  };
  var handleInputFocus = function handleInputFocus(e) {
    updateFocusInfo({
      inputHasFocus: true
    });
    setFocusInfo(function (prevFocusInfo) {
      if (!prevFocusInfo.userClicked) {
        var _inputRef$current4;
        (_inputRef$current4 = _inputRef.current) === null || _inputRef$current4 === void 0 || _inputRef$current4.select();
      }
      return prevFocusInfo;
    });
    onFocus && onFocus(e);
    setMenuIsOpen(true);
  };
  var handleInputBlur = function handleInputBlur(e) {
    hadClickFocusOnInputBlurRef.current = !!focusInfo.userClicked;
    updateFocusInfo({
      inputHasFocus: false,
      userClicked: false
    });
    onBlur && onBlur(e);
  };
  var handleInputPointerDown = function handleInputPointerDown(e) {
    // preventDefault() is necessary on touch devices to prevent a pop-up
    //  lookup menu from showing - see EMBERS-223 - but it
    //  breaks the "double-click to select" on mouse-devices
    if (isTouchDevices()) {
      e.preventDefault();
    }
    updateFocusInfo({
      inputHasFocus: true,
      userClicked: true
    });
  };
  var handleInputClick = function handleInputClick() {
    handleOpenMenuRequest();
  };
  var handleTagDelete = function handleTagDelete(option) {
    // we only get this callback if the user clicked on the closeX of the tag
    //  so it is a click event
    handleItemClick({
      type: 'click'
    }, option);
  };
  var renderTags = function renderTags() {
    if (selectedOptionIndexes.length > 0) {
      return selectedOptionIndexes.map(function (index) {
        var selectedOption = options[index];
        return /*#__PURE__*/React.createElement(EmbersTag, {
          role: "none",
          id: "Select-".concat(id, "-MultiTags-").concat(index),
          className: classnames({
            'InputSelect-tag': true,
            'InputSelect-tag-disabled': disabled
          }),
          key: index,
          label: getOptionLabel(selectedOption),
          deleteIcon: /*#__PURE__*/React.createElement("span", {
            "aria-hidden": true,
            className: "icon-close-alt"
          }),
          onDelete: function onDelete() {
            handleTagDelete(selectedOption);
          },
          tabIndex: -1
        });
      });
    }
  };
  var makeNextItemActive = function makeNextItemActive() {
    var newActiveIndex = 0;
    if (currentActiveDescendentId) {
      var currentIndex = itemIdsRef.current.indexOf(currentActiveDescendentId);
      if (currentIndex !== -1 && currentIndex < itemIdsRef.current.length - 1) {
        newActiveIndex = currentIndex + 1;
      }
    }
    var newCurrentItemId = itemIdsRef.current[newActiveIndex];
    setCurrentActiveDescendentId(newCurrentItemId);
    scrollMenuItemIntoView(newCurrentItemId);
  };
  var makePreviousItemActive = function makePreviousItemActive() {
    var newActiveIndex = itemIdsRef.current.length - 1;
    if (currentActiveDescendentId) {
      var currentIndex = itemIdsRef.current.indexOf(currentActiveDescendentId);
      if (currentIndex !== -1 && currentIndex > 0) {
        newActiveIndex = currentIndex - 1;
      }
    }
    var newCurrentItemId = itemIdsRef.current[newActiveIndex];
    setCurrentActiveDescendentId(newCurrentItemId);
    scrollMenuItemIntoView(newCurrentItemId);
  };
  var getCurrentlyActiveOption = function getCurrentlyActiveOption() {
    if (currentActiveDescendentId) {
      var currentIndex = itemIdsRef.current.indexOf(currentActiveDescendentId);
      if (blankOption && selectedOptionIndexes.length > 0) {
        // if there is a blank option and a selection has been made then the first
        //  item in the itemIdsRef is the blankOption
        if (currentIndex === 0) {
          return blankOption;
        }
        currentIndex = currentIndex - 1;
      }
      // if there are dividers they are not in the itemIds but the ARE in
      //  the filteredOptions so adjust for that
      if (optionsAreMaps) {
        var actualOptionIndex = currentIndex;
        for (var i = 0; i <= currentIndex; i++) {
          var _filteredOptions$i;
          if ((_filteredOptions$i = filteredOptions[i]) !== null && _filteredOptions$i !== void 0 && _filteredOptions$i.isDivider) {
            actualOptionIndex++;
          }
        }
        currentIndex = actualOptionIndex;
      }
      return filteredOptions[currentIndex];
    }
    return null;
  };
  var handleInputKeydown = function handleInputKeydown(e) {
    if (menuIsOpen) {
      if (e.key === 'Escape' || e.key === 'Enter' && multiSelect) {
        handleCloseMenuRequest();
        return;
      }
    }
    // this is a test for "is it a printable key versus shift ctrl etc"
    //  because we don't want to open the menu etc just for the ctrl key
    if (e.key.length === 1) {
      handleOpenMenuRequest();
    }
    // all the edit keys should open the menu too because they could affect the filter value
    switch (e.key) {
      case 'Delete':
      case 'Clear':
      case 'Cut':
      case 'Backspace':
      case 'EraseEof':
      case 'Paste':
      case 'Redo':
      case 'Undo':
        handleOpenMenuRequest();
        break;
      case 'ArrowDown':
        e.stopPropagation();
        handleOpenMenuRequest();
        makeNextItemActive();
        break;
      case 'ArrowUp':
        e.stopPropagation();
        handleOpenMenuRequest();
        makePreviousItemActive();
        break;
      case 'Enter':
        if (!multiSelect) {
          e.stopPropagation();
          // single-select Enter key is as if clicking on the option
          var currentlyActiveOption = getCurrentlyActiveOption();
          if (currentlyActiveOption) {
            handleItemClick(e, currentlyActiveOption);
          }
        }
        break;
      case ' ':
        if (multiSelect) {
          e.stopPropagation();
          // multi-select space key is as if clicking on the option
          var _currentlyActiveOption = getCurrentlyActiveOption();
          if (_currentlyActiveOption) {
            spaceInputWasMultiSelectAction.current = true;
            handleItemClick(e, _currentlyActiveOption);
          }
        }
        break;
      default:
        break;
    }
  };
  var clearSelection = function clearSelection() {
    updateSelectionInfo([]);
    setBlankOptionIsSelected(false);
    if (onChange) {
      if (optionsAreMaps) {
        var _ref;
        var emptyObj = customOptionLabel && customOptionValue ? (_ref = {}, _defineProperty(_ref, customOptionLabel, ''), _defineProperty(_ref, customOptionValue, ''), _ref) : {
          value: '',
          label: ''
        };
        onChange(emptyObj);
      } else onChange('');
    }
  };
  var handleInputChange = function handleInputChange(e) {
    var newValue = e.target.value;
    if (spaceInputWasMultiSelectAction.current) {
      // if multi-select and a menu item is active, the space key acts as
      //  click on keydown, and we should not add the space to the input value
      spaceInputWasMultiSelectAction.current = false;
      return;
    }
    if (newValue === ' ') {
      newValue = '';
    }
    setSearchResults('');
    setInputValue(newValue);
    setUseInputAsFilter(true);
    // if the new input does not match the selection of a single-select
    //  then clear the selection
    if (!multiSelect && !noEmptySelection) {
      if (newValue === '' || !doesAnyOptionMatch(e.target.value)) {
        clearSelection();
      }
    }
  };
  var handleLaunchButtonClick = function handleLaunchButtonClick() {
    updateFocusInfo({
      inputHasFocus: true,
      userClicked: true
    });
    setTimeout(function () {
      var _inputRef$current5;
      (_inputRef$current5 = _inputRef.current) === null || _inputRef$current5 === void 0 || _inputRef$current5.focus();
    }, 10);
  };
  var renderButton = function renderButton() {
    var _selectedTags;
    var legendElem = null;
    var darkLabelElem = null;
    var alertLightFocusFieldset;
    var selectedTags;
    // let placeholderText
    if (labelIsInCorner || isTabFocused() && !currentActiveDescendentId) {
      if (isDarkMode) {
        darkLabelElem = /*#__PURE__*/React.createElement(InputSelectDarkLabel, {
          className: classnames({
            'EmbersInputSelect-darkLabel-disabled': disabled,
            'EmbersInputSelect-darkLabel-required': required
          })
        }, required ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, selectLabel), /*#__PURE__*/React.createElement("span", {
          "aria-hidden": "true",
          className: "EmbersInputSelect-darkLabel-required-postfix"
        }, "\xA0*")) : selectLabel);
      } else if (required) {
        legendElem = /*#__PURE__*/React.createElement("legend", null, /*#__PURE__*/React.createElement("span", null, selectLabel), /*#__PURE__*/React.createElement("span", {
          "aria-hidden": "true",
          className: "EmbersInputSelect-legend-requiredPostfix"
        }, "\xA0*"));
      } else {
        legendElem = /*#__PURE__*/React.createElement("legend", null, selectLabel);
      }
    }
    if (labelIsInCorner && multiSelect) {
      selectedTags = renderTags();
    }

    // the alert-style selects in light mode have an internal outline when tab-focussed
    //   which is drawn by means of a second fieldset
    if (isTabFocused() && !menuIsOpen && !isDarkMode && (errorMessage || successMessage)) {
      // if there is a label in the corner we need to make a blank legend that
      //  interrupts this interior fieldset to leave room for the label
      var spacerLegend;
      if (labelIsInCorner) {
        var labelFontDesc = {
          family: prometheanFont,
          size: 12,
          weight: 400,
          letterSpacing: 0.32
        };
        var cornerLabelWidth = getTextWidth(selectLabel, labelFontDesc);
        spacerLegend = /*#__PURE__*/React.createElement("legend", {
          className: "spacer-legend",
          style: {
            minWidth: cornerLabelWidth + 10
          }
        });
      }
      alertLightFocusFieldset = /*#__PURE__*/React.createElement(InputSelectAlertFocusFieldset, {
        className: classnames({
          'SelectLaunchButton-alertFieldset-disabled': disabled,
          'SelectLaunchButton-alertFieldset-error': !!errorMessage,
          'SelectLaunchButton-alertFieldset-success': !!successMessage
        })
      }, spacerLegend);
    }
    var rightIcon;
    if (loading) {
      rightIcon = /*#__PURE__*/React.createElement(EmbersLoading, {
        size: 'small'
      });
    } else {
      rightIcon = /*#__PURE__*/React.createElement("span", {
        "aria-hidden": true,
        className: classnames({
          'SelectLaunchButton-arrowIcon': true,
          'SelectLaunchButton-arrowIcon-disabled': disabled,
          'icon-expand-more': true,
          'is-open': menuIsOpen
        }),
        onClick: handleArrowIconClick
      });
    }
    var disableInput = disabled || loading;

    /**
     * Render the icon only when showFieldPrefixIcon is true, and the value is an object including `prefixIcon`.
     * And when changing the value of input, hide the icon because the current input value may not have the same meaning with the icon
     */
    var renderFieldPrefixIcon = function renderFieldPrefixIcon() {
      if (showFieldPrefixIcon && value && typeof value !== 'string' && !(value instanceof Array) && !useInputAsFilter) {
        if (typeof value.prefixIcon === 'string') return /*#__PURE__*/React.createElement("span", {
          className: value.prefixIcon,
          title: value.prefixIcon
        });else return value.prefixIcon;
      }
      return null;
    };
    return /*#__PURE__*/React.createElement(StyledInputSelectLaunchButton, {
      id: "SelectLaunchButton-".concat(id),
      ref: menuAnchorRef,
      className: classnames({
        'SelectLaunchButton-root': true,
        'SelectLaunchButton-disabled': disabled || loading,
        'SelectLaunchButton-needsTopSpace': multiSelect && hasSelectionRef.current && !!darkLabelElem,
        'SelectLaunchButton-dark': isDarkMode
      }),
      onClick: handleLaunchButtonClick
    }, darkLabelElem, /*#__PURE__*/React.createElement(InputSelectFieldset, {
      id: 'SelectInput-' + id,
      "aria-hidden": true,
      className: classnames({
        'SelectLaunchButton-fieldset-disabled': disabled,
        'SelectLaunchButton-fieldset-dark': isDarkMode,
        'SelectLaunchButton-fieldset-emptyLegend': !labelIsInCorner || isDarkMode,
        'SelectLaunchButton-fieldset-menuIsOpen': menuIsOpen,
        'SelectLaunchButton-fieldset-focusVisible': isTabFocused() && !currentActiveDescendentId,
        'SelectLaunchButton-fieldset-error': !!errorMessage,
        'SelectLaunchButton-fieldset-success': !!successMessage
      })
    }, legendElem), alertLightFocusFieldset, selectedTags, renderFieldPrefixIcon(), /*#__PURE__*/React.createElement(StyledInputSelectLabel, {
      id: "SelectInput-label-".concat(id),
      className: classnames({
        'EmbersInputSelect-label': true,
        'EmbersInputSelect-label-labelIsInCorner': labelIsInCorner,
        'EmbersInputSelect-label-menuOpen': menuIsOpen,
        'EmbersInputSelect-label-focusVisible': isTabFocused() && !currentActiveDescendentId,
        'EmbersInputSelect-label-disabled': disabled,
        'EmbersInputSelect-label-required': required,
        'EmbersInputSelect-label-blankOptionSelected': blankOptionIsSelected
      }),
      onClick: function onClick() {
        return setMenuIsOpen(true);
      }
    }, required ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
      className: "EmbersInputSelect-label-required-text"
    }, selectLabel), /*#__PURE__*/React.createElement("span", {
      "aria-hidden": "true",
      className: "EmbersInputSelect-label-required-postfix"
    }, "\xA0*")) : selectLabel), /*#__PURE__*/React.createElement(StyledInputSelectInput, _extends({
      className: classnames({
        'SelectLaunchButton-input': true,
        'SelectLaunchButton-input-labelIsInBox': !labelIsInCorner,
        'SelectLaunchButton-input-disabled': disabled,
        'SelectLaunchButton-input-placeholderUp': labelIsInCorner,
        'SelectLaunchButton-input-inputIsFilter': useInputAsFilter,
        'SelectLaunchButton-input-dark': isDarkMode
      }),
      style: {
        minWidth: (multiSelect ? (value === null || value === void 0 ? void 0 : value.length) > 0 : value) ? 0 : inputMinWidth
      },
      role: "combobox",
      "aria-labelledby": multiSelect && hasSelectionRef.current ? "SelectInput-label-".concat(id, " ").concat((_selectedTags = selectedTags) === null || _selectedTags === void 0 ? void 0 : _selectedTags.map(function (tag) {
        return "Select-".concat(id, "-MultiTags-").concat(tag.key);
      }).toString().replace(/,/g, ' '), " menu-alertText-").concat(id, " menu-helpText-").concat(id) : "SelectInput-label-".concat(id, " menu-helpText-").concat(id),
      "aria-controls": menuIsOpen ? "menu-".concat(id) : undefined,
      "aria-haspopup": "listbox",
      "aria-expanded": menuIsOpen,
      "aria-autocomplete": "list",
      "aria-busy": loading,
      "aria-activedescendant": currentActiveDescendentId !== null && currentActiveDescendentId !== void 0 ? currentActiveDescendentId : undefined,
      "aria-required": required,
      "aria-invalid": !!errorMessage,
      type: "text",
      tabIndex: disabled ? -1 : tabIndex
      // placeholder={placeholderText}
      ,
      autoComplete: "off",
      autoCapitalize: "none",
      spellCheck: false,
      value: inputValue,
      ref: _inputRef,
      disabled: disableInput,
      onFocus: handleInputFocus,
      onPointerDown: handleInputPointerDown,
      onClick: handleInputClick,
      onBlur: handleInputBlur,
      onKeyDown: handleInputKeydown,
      onChange: handleInputChange
    }, inputProps)), rightIcon);
  };

  // NOTE:  the `key` on EmbersMenu is so that when menu length changes it re-renders
  return /*#__PURE__*/React.createElement(InputSelectContainer, _extends({
    ref: ref,
    className: classnames('InputSelect-root', className),
    id: id
  }, restProps), renderButton(), /*#__PURE__*/React.createElement(EmbersMenu, {
    id: "menu-".concat(id),
    key: 'menuKey_' + filteredOptions.length,
    anchorEl: menuAnchorRef.current,
    open: menuIsOpen && !loading,
    onClose: handleCloseMenuRequest,
    fullWidth: true,
    maxMenuHeight: maxMenuHeight,
    verticalOffset: 0,
    disableCloseOnWindowResize: disableCloseOnWindowResize,
    ariaProps: {
      'aria-multiselectable': multiSelect ? 'true' : 'false'
    }
  }, renderMenuItems()), /*#__PURE__*/React.createElement("div", {
    id: "menu-liveRegion-".concat(id),
    style: visuallyHidden,
    role: "status",
    "aria-live": "polite",
    "aria-atomic": "true",
    "aria-relevant": searchResults ? 'all' : 'additions text'
  }, searchResults), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, renderAlertText(), renderHelperText()));
}
export var EmbersInputSelect = /*#__PURE__*/React.forwardRef(InputSelect);